'use client';

import { ReactNode, useId, useRef, useState } from 'react';

import { css } from 'styled-system/css';
import { button } from 'styled-system/recipes';

import { Reveal } from '@/rsc/client/Reveal';
import { Heading } from '@/rsc/ui/Heading';
import { Text } from '@/rsc/ui/Text';

import { PlusMinusIconAnimated } from './PlusMinusIconAnimated';

export type QAAccordionItem = { header: string; text: ReactNode };

type Props = {
  /** Server-rendered section heading (Reveal + h2). */
  heading: ReactNode;
  items: QAAccordionItem[];
  showMoreLabel: string;
  showLessLabel: string;
};

const INITIAL_ITEMS_COUNT = 5;

const containerClass = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  maxW: { base: '100%', md: '792px' },
  justifyContent: 'center',
  alignItems: 'center',
  mx: 'auto',
  // Keeps the scrollIntoView target clear of the fixed header (HEADER_HEIGHT = 80px)
  scrollMarginTop: 'calc(80px + 16px)',
});

const itemClass = css({
  borderBottomWidth: '1px',
  borderBottomColor: 'divider',
  pt: '28px',
  pb: '28px',
  px: '12px',
  position: 'relative',
  display: 'block',
  '&[data-hidden]': { display: 'none' },
  _before: {
    content: '""',
    position: 'absolute',
    top: '4px',
    left: 0,
    right: 0,
    bottom: '4px',
    bg: 'bg-secondary',
    opacity: 0,
    transition: 'opacity 0.2s ease-in-out',
  },
  _hover: { _before: { opacity: 1 } },
});

const questionButtonClass = css({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'space-between',
  textAlign: 'start',
  bg: 'transparent',
  border: 'none',
  p: 0,
  cursor: 'pointer',
  color: 'inherit',
  font: 'inherit',
  _focusVisible: { boxShadow: 'outline' },
});

const iconBoxClass = css({
  display: 'flex',
  minW: '48px',
  minH: '48px',
  ml: 2,
  alignItems: 'center',
  justifyContent: 'center',
});

const panelClass = css({
  maxWidth: { base: '100%', md: 'calc(100% - 80px)' },
  position: 'relative',
  zIndex: 1,
  mt: 0,
  maxH: 0,
  overflow: 'hidden',
  opacity: 0,
  transition: 'all 0.2s ease-in-out',
  '&[data-open]': { mt: 4, maxH: 'none', opacity: 1 },
});

// Low-emphasis text-like control under the list → the design system's ghost button.
const showMoreButtonClass = button({ variant: 'ghost', size: 'md' });

/**
 * Client half of services/sections/qa/qa-section: the accordion + "show more" state. Every
 * answer is in the SSR HTML even when collapsed (max-height 0 + opacity 0, as the original).
 */
export const QAAccordion = ({ heading, items, showMoreLabel, showLessLabel }: Props) => {
  // Unique per section instance, so aria-controls/aria-labelledby id pairs stay valid
  // even if two QASections ever land on one page
  const sectionId = useId();
  const [showAll, setShowAll] = useState(false);
  const [expandedItems, setExpandedItems] = useState<number[]>([]);
  const sectionTopRef = useRef<HTMLDivElement | null>(null);

  const shouldShowSeeMore = items.length > INITIAL_ITEMS_COUNT;

  const handleItemClick = (index: number) => {
    setExpandedItems((current) => (current.includes(index) ? current.filter((i) => i !== index) : [...current, index]));
  };

  const handleToggleShowAll = () => {
    if (showAll) {
      // Collapsing rips the extra items out of the layout while the viewport keeps
      // its scroll position — jump back to the top of the FAQ instead.
      const behavior: ScrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth';
      sectionTopRef.current?.scrollIntoView({ behavior, block: 'start' });
    }
    setShowAll((prev) => !prev);
  };

  return (
    <div ref={sectionTopRef} className={containerClass}>
      <div className={css({ display: 'flex' })}>{heading}</div>
      <div className={css({ display: 'flex', flex: { base: 1.5, lg: 1 }, mt: -4, width: '100%' })}>
        <div className={css({ width: '100%' })} id={`${sectionId}-faq-list`}>
          {items.map((item, index) => {
            const isVisible = !shouldShowSeeMore || showAll || index < INITIAL_ITEMS_COUNT;
            const isOpen = expandedItems.includes(index);
            return (
              // Each item reveals on its own viewport entry — items unhidden by "show more"
              // animate in the moment their display flips, since IO fires only once visible.
              <Reveal key={`faq-item-${index}`}>
                <div className={itemClass} data-hidden={isVisible ? undefined : ''}>
                  {/* WAI-ARIA accordion pattern: the whole question is a <button> inside the <h3>. */}
                  <Heading
                    as="h3"
                    variant={{
                      base: 'text-lg/lineHeight-7/font-semibold',
                      sm: 'component-article-headline/2xl',
                      md: 'text-lg/lineHeight-8/font-normal',
                    }}
                    className={css({ color: 'text-dark.500', position: 'relative', zIndex: 1 })}
                  >
                    <button
                      type="button"
                      id={`${sectionId}-q-${index}`}
                      aria-expanded={isOpen}
                      aria-controls={`${sectionId}-panel-${index}`}
                      onClick={() => handleItemClick(index)}
                      className={questionButtonClass}
                    >
                      <span className={css({ flex: '1' })}>{item.header}</span>
                      <span className={iconBoxClass} aria-hidden>
                        <PlusMinusIconAnimated plus={!isOpen} />
                      </span>
                    </button>
                  </Heading>

                  <div
                    id={`${sectionId}-panel-${index}`}
                    role="region"
                    aria-labelledby={`${sectionId}-q-${index}`}
                    className={panelClass}
                    data-open={isOpen ? '' : undefined}
                  >
                    <Text
                      as="div"
                      variant="text-md/lineHeight-24/font-normal"
                      className={css({ color: 'text-dark.300' })}
                    >
                      {item.text}
                    </Text>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
      {shouldShowSeeMore && (
        <div className={css({ display: 'flex', justifyContent: 'center', mb: 10 })}>
          <button
            type="button"
            className={showMoreButtonClass}
            onClick={handleToggleShowAll}
            // This control reveals the remaining questions, so it is a disclosure.
            aria-expanded={showAll}
            aria-controls={`${sectionId}-faq-list`}
          >
            {showAll ? showLessLabel : showMoreLabel}
          </button>
        </div>
      )}
    </div>
  );
};
